import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)")
  );
  return m ? decodeURIComponent(m[1]) : null;
}

export function setCookie(name: string, value: string, maxAgeSeconds = 60 * 60 * 24 * 7) {
  if (typeof document === "undefined") return;
  document.cookie =
    `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; samesite=lax`;
}

// ---------------------------------------------------------------------------
// Run error classification
// ---------------------------------------------------------------------------

// The playground backend can mark a run status="error" while still saving a
// full report — e.g. a code-producing playbook whose LLM step didn't emit a
// parseable file. Mirrors app/main.py's _PLAYGROUND_SOFT_ERROR_MESSAGES on the
// backend: these are completed runs with a real report, not failures, so the
// report must still render alongside the caveat.
const SOFT_RUN_ERROR_MESSAGES = new Set(["generated_files_missing"]);

export function isSoftRunError(errorMessage: string | null | undefined): boolean {
  return Boolean(errorMessage && SOFT_RUN_ERROR_MESSAGES.has(errorMessage));
}

// `agent_runs.error_message` / `runs.error_message` hold a machine code, not a
// sentence — "llm_budget_exceeded", "run_failed:TimeoutError". Rendering the raw
// code is still better than the ERROR badge alone (it is the only lead the user
// gets), so the fallback is VERBATIM: an unrecognised code is passed through
// untouched rather than flattened into a useless "Something went wrong".
// A Map, not an object literal — a wire value of "constructor" must not resolve
// to something off Object.prototype.
const RUN_ERROR_LABELS = new Map<string, string>([
  ["llm_budget_exceeded", "LLM budget exceeded — the run was stopped before a report was generated"],
  ["run_timeout", "Run timed out"],
  ["generated_files_missing", "Run finished but expected generated files were missing"],
  ["input_required", "Run needs more input"],
]);

// The runner prefixes an uncaught exception with this; everything after the FIRST
// colon names the failure (it can itself contain colons, e.g.
// "run_failed:httpx.ReadTimeout: connect").
const RUN_FAILED_PREFIX = "run_failed:";

/**
 * Human-readable label for a run's error code, or `null` when the run did not
 * fail. Never invents text: a run with no error message renders nothing.
 */
export function runErrorLabel(errorMessage: string | null | undefined): string | null {
  if (!errorMessage) return null;

  const known = RUN_ERROR_LABELS.get(errorMessage);
  if (known) return known;

  if (errorMessage.startsWith(RUN_FAILED_PREFIX)) {
    const exception = errorMessage.slice(RUN_FAILED_PREFIX.length).trim();
    // No exception name to report — "Run failed ()" reads as a UI bug, so keep
    // the raw code instead.
    if (exception) return `Run failed (${exception})`;
  }

  return errorMessage;
}

// ---------------------------------------------------------------------------
// Misc formatting helpers
// ---------------------------------------------------------------------------

const RELATIVE_TIME = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 24 * 60 * 60_000],
  ["month", 30 * 24 * 60 * 60_000],
  ["week", 7 * 24 * 60 * 60_000],
  ["day", 24 * 60 * 60_000],
  ["hour", 60 * 60_000],
  ["minute", 60_000],
  ["second", 1_000],
];

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  // Backend timestamps come back naive ("2026-06-28 02:38:07" — no zone) but are
  // UTC. Date.parse() treats a zone-less value as LOCAL, so a run that just
  // happened reads as "in 4 hours". Mark zone-less values UTC before parsing.
  // (Plain string ops — no regex/.replace/.split.)
  let s = iso.trim();
  const sep = s.indexOf("T") >= 0 ? s.indexOf("T") : s.indexOf(" ");
  const timePart = sep >= 0 ? s.slice(sep + 1) : "";
  const zoned = s.endsWith("Z") || timePart.includes("+") || timePart.includes("-");
  if (sep >= 0 && !zoned) {
    s = s.slice(0, sep) + "T" + s.slice(sep + 1) + "Z";
  }
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return "—";
  const diff = t - Date.now();
  const abs = Math.abs(diff);
  for (const [unit, ms] of RELATIVE_UNITS) {
    if (abs >= ms || unit === "second") {
      return RELATIVE_TIME.format(Math.round(diff / ms), unit);
    }
  }
  return RELATIVE_TIME.format(0, "second");
}

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * True for the `.html` of an LWC bundle — identified by a sibling
 * `<base>.js-meta.xml` among the run's artifacts, not by a name pattern.
 *
 * An LWC template is SOURCE, not a page. Previewed as text/html the browser
 * RENDERS it, and a file whose whole body sits inside `<template>` renders as a
 * blank tab, which reads as "nCoder generated an empty file" (reported
 * 2026-09-17). A standalone `.html` is a report export and must still render.
 */
export function isLwcTemplate(name: string, allNames: string[]): boolean {
  const lower = name.toLowerCase();
  if (!lower.endsWith(".html")) return false;
  const base = lower.slice(0, -".html".length);
  return allNames.some((item) => item.toLowerCase() === base + ".js-meta.xml");
}

/** Encode a JSON-serialisable value as URL-safe base64. */
export function encodeBase64Json(value: unknown): string {
  const json = JSON.stringify(value ?? {});
  if (typeof window === "undefined") {
    return Buffer.from(json, "utf-8").toString("base64");
  }
  // Handle UTF-8 safely: encodeURIComponent + escape trick.
  const utf8 = unescape(encodeURIComponent(json));
  return window.btoa(utf8);
}

export function decodeBase64Json<T = unknown>(b64: string): T | null {
  try {
    if (!b64) return null;
    const raw =
      typeof window === "undefined"
        ? Buffer.from(b64, "base64").toString("utf-8")
        : decodeURIComponent(escape(window.atob(b64)));
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}
